<script setup lang="ts">
import { computed } from 'vue';
import type { Component } from 'vue';
import * as LucideIcons from 'lucide-vue-next';
import {
  CircleCheckBig,
  Info,
  Home,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon = (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileCheck;
const TrendingUpIcon = (LucideIcons as Record<string, Component>).TrendingUp;
const AwardIcon = (LucideIcons as Record<string, Component>).Award;

/** Matches backend STAT_CARD_ICON_CHOICES (HELP + trending-up, award). */
const STAT_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  info: Info,
  home: Home,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon ?? FileCheck,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
  'trending-up': TrendingUpIcon ?? Info,
  award: AwardIcon ?? Info,
};

type Accent = { rgb: string };

/** Prototype StatsBar: primary, secondary, orange — then other process colours. */
const ACCENT_CYCLE: Accent[] = [
  { rgb: '4, 120, 87' },
  { rgb: '124, 58, 237' },
  { rgb: '234, 88, 12' },
  { rgb: '8, 145, 178' },
  { rgb: '217, 119, 6' },
];

function iconComponent(iconName: string | null | undefined): Component {
  if (!iconName || typeof iconName !== 'string') return Info;
  return STAT_ICON_MAP[iconName] ?? Info;
}

function accentForIndex(index: number): Accent {
  return ACCENT_CYCLE[index % ACCENT_CYCLE.length]!;
}

type StatCardApi = {
  icon?: string | null;
  stat_number?: string | null;
  stat_description?: string | null;
};

const props = withDefaults(
  defineProps<{
    /** API: array of cards. Block-picker preview: JSON string in query `cards`. */
    cards?: StatCardApi[] | string | null;
  }>(),
  { cards: () => [] }
);

function normalizeCardsInput(raw: StatCardApi[] | string | null | undefined): StatCardApi[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as StatCardApi[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const cardList = computed(() => {
  const raw = normalizeCardsInput(props.cards);
  return raw.filter(
    (c) =>
      c &&
      (String(c.stat_number ?? '').trim() !== '' ||
        String(c.stat_description ?? '').trim() !== '')
  );
});
</script>

<template>
  <div v-if="cardList.length >= 2" class="stat-cards-block">
    <div class="stat-cards-block__grid" role="list">
      <div
        v-for="(card, index) in cardList"
        :key="index"
        class="stat-cards-block__card"
        role="listitem"
        :style="{
          '--stat-accent-rgb': accentForIndex(index).rgb,
        }"
      >
        <div class="stat-cards-block__icon-wrap" aria-hidden="true">
          <component :is="iconComponent(card.icon)" class="stat-cards-block__icon" />
        </div>
        <div v-if="card.stat_number" class="stat-cards-block__number">
          {{ card.stat_number }}
        </div>
        <p v-if="card.stat_description" class="stat-cards-block__desc">
          {{ card.stat_description }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stat-cards-block {
  width: 100%;
}

.stat-cards-block__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .stat-cards-block__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2rem;
  }
}

.stat-cards-block__card {
  --stat-accent-rgb: 4, 120, 87;
  --a: var(--stat-accent-rgb);
  text-align: center;
  padding: 2rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(var(--a), 0.2);
  background: linear-gradient(
    to bottom right,
    rgba(var(--a), 0.1),
    rgba(var(--a), 0.05),
    transparent
  );
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease;
}

.stat-cards-block__card:hover {
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.08),
    0 4px 6px -4px rgba(0, 0, 0, 0.06);
  transform: translateY(-4px);
}

.stat-cards-block__icon-wrap {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  border-radius: 0.75rem;
  background: rgba(var(--a), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-cards-block__icon {
  width: 2rem;
  height: 2rem;
  color: rgb(var(--a));
}

.stat-cards-block__number {
  margin: 0 0 0.5rem;
  font-weight: 700;
  line-height: 1.15;
  color: rgb(var(--a));
  font-size: 2.25rem;
}

@media (min-width: 1024px) {
  .stat-cards-block__number {
    font-size: 3rem;
  }
}

.stat-cards-block__desc {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  color: color-mix(in srgb, var(--color-text, #1a1a1a) 80%, transparent);
}

@media (min-width: 1024px) {
  .stat-cards-block__desc {
    font-size: 1.125rem;
  }
}
</style>
